/**
 * LoyaltyLeaderboardCard — workspace card ranking THIS seller's top scouts by
 * total reactions across their active AI Cart shares, grouped by VIP tier
 * (🥇 Gold ≥30 · 🥈 Silver ≥15 · 🥉 Bronze ≥5) with an inline "Thank" flow
 * that hits the existing `/ai-cart/shares/mine/leaderboard/thank` endpoint.
 *
 * Data source: `GET /api/ai-cart/shares/mine/leaderboard` — already returns
 * every voter with tier/tier_medal/tier_discount_pct fields, plus a
 * `tier_counts` object so we can render the stat strip without recomputing.
 *
 * Zero new backend work — pure UI card that composes existing endpoints.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, Loader2, Heart, MessageSquareHeart, Send, X, RefreshCw, Award,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

// ---- Types kept identical to the backend payload -----------------------
interface LeaderboardEntry {
  voter_id: string;
  voter_user_id?: string | null;
  has_phone?: boolean;
  name: string;
  is_anonymous: boolean;
  is_loyal_friend?: boolean;
  tier: "bronze" | "silver" | "gold" | null;
  tier_label: string | null;
  tier_medal: string | null;
  tier_discount_pct: number | null;
  up: number; think: number; nope: number;
  total_reactions: number;
  distinct_items: number;
  distinct_shares: number;
  first_at: string | null;
  last_at: string | null;
}

interface TierCounts {
  gold: number; silver: number; bronze: number; below: number;
}

interface LeaderboardPayload {
  leaderboard: LeaderboardEntry[];
  total_voters: number;
  tier_counts: TierCounts;
}

// Palette per tier — matches SharedCart + Order confirmation banner.
const TIER_STYLE = {
  gold:   { chip: "bg-yellow-100 text-yellow-800 border-yellow-400",
            row: "border-l-yellow-400" },
  silver: { chip: "bg-slate-100 text-slate-700 border-slate-300",
            row: "border-l-slate-400" },
  bronze: { chip: "bg-amber-50 text-amber-800 border-amber-300",
            row: "border-l-amber-500" },
  below:  { chip: "bg-neutral-100 text-neutral-500 border-neutral-200",
            row: "border-l-neutral-300" },
} as const;

export const LoyaltyLeaderboardCard: React.FC = () => {
  const [data, setData] = useState<LeaderboardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [thankTarget, setThankTarget] = useState<LeaderboardEntry | null>(null);
  const [thankMessage, setThankMessage] = useState("");
  const [thankChannel, setThankChannel] = useState<"whatsapp" | "sms">("whatsapp");
  const [thankSending, setThankSending] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<LeaderboardPayload>("/ai-cart/shares/mine/leaderboard")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadTick]);

  // Group voters into 4 buckets so the UI renders tier-tier sections rather
  // than one flat table — much easier for sellers to spot their real MVPs.
  const grouped = useMemo(() => {
    const buckets: Record<"gold" | "silver" | "bronze" | "below", LeaderboardEntry[]> = {
      gold: [], silver: [], bronze: [], below: [],
    };
    for (const v of data?.leaderboard || []) {
      buckets[(v.tier as any) || "below"].push(v);
    }
    return buckets;
  }, [data]);

  async function openThank(entry: LeaderboardEntry) {
    setThankTarget(entry);
    setThankMessage("");
    try {
      const { data } = await api.get<{ template: string }>(
        "/ai-cart/shares/mine/leaderboard/thank/template",
        { params: { voter_id: entry.voter_id } });
      setThankMessage(data.template || "");
    } catch { /* seller can compose from scratch */ }
  }

  async function sendThank() {
    if (!thankTarget || thankMessage.trim().length < 4) return;
    setThankSending(true);
    try {
      const { data } = await api.post("/ai-cart/shares/mine/leaderboard/thank", {
        voter_id: thankTarget.voter_id,
        channel: thankChannel,
        message: thankMessage.trim(),
      });
      if (data?.ok) {
        toast.success(`Thank-you card delivered to ${thankTarget.name}`);
        setThankTarget(null);
      } else {
        toast.error(data?.error || "Twilio couldn't deliver that message");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Thank-you note failed");
    } finally {
      setThankSending(false);
    }
  }

  return (
    <div
      className="rounded-2xl border border-neutral-200 bg-white overflow-hidden mb-6"
      data-testid="loyalty-leaderboard-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 gap-3 bg-gradient-to-r from-white to-amber-50/30">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-neutral-500 flex items-center gap-1.5">
            <Award className="h-3 w-3 text-amber-600" /> Loyalty Leaderboard
          </div>
          <div className="text-[11px] text-neutral-500 mt-0.5">
            Your top scouts across every AI Cart™ you've shared. Thank them, and their next reaction feels earned.
          </div>
        </div>
        <button
          onClick={() => setReloadTick(t => t + 1)}
          data-testid="loyalty-leaderboard-refresh"
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 px-2 py-1 text-[11px] font-medium hover:bg-neutral-50"
          title="Refresh"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Tier count strip */}
      <div className="grid grid-cols-4 divide-x divide-neutral-200 border-b border-neutral-200 bg-white">
        <TierCountCell label="Gold"   count={data?.tier_counts?.gold   ?? 0} medal="🥇" testid="loyalty-count-gold"   accent="text-yellow-700" />
        <TierCountCell label="Silver" count={data?.tier_counts?.silver ?? 0} medal="🥈" testid="loyalty-count-silver" accent="text-slate-600" />
        <TierCountCell label="Bronze" count={data?.tier_counts?.bronze ?? 0} medal="🥉" testid="loyalty-count-bronze" accent="text-amber-700" />
        <TierCountCell label="Rising" count={data?.tier_counts?.below  ?? 0} medal="✨" testid="loyalty-count-below"  accent="text-neutral-500" />
      </div>

      {loading ? (
        <div className="p-10 text-center text-neutral-500 text-sm">
          <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading your top scouts…
        </div>
      ) : (data?.total_voters ?? 0) === 0 ? (
        <div className="p-10 text-center text-neutral-500 text-sm">
          No reactions yet. Share an AI Cart™ with friends to start earning loyal scouts.
        </div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {(["gold", "silver", "bronze", "below"] as const).map(k => {
            const rows = grouped[k];
            if (!rows.length) return null;
            const label = k === "below" ? "Rising · under 5 reactions" : `${k[0].toUpperCase() + k.slice(1)} · ${k === "gold" ? "30+" : k === "silver" ? "15-29" : k === "bronze" ? "5-14" : "1-4"} reactions`;
            return (
              <section
                key={k}
                data-testid={`loyalty-leaderboard-section-${k}`}
                className="py-2"
              >
                <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-1.5">
                  <span aria-hidden>{k === "gold" ? "🥇" : k === "silver" ? "🥈" : k === "bronze" ? "🥉" : "✨"}</span>
                  {label} <span className="ml-1 text-neutral-400">({rows.length})</span>
                </div>
                <ul>
                  {rows.map((v, i) => (
                    <li
                      key={v.voter_id}
                      className={`px-4 py-3 flex items-center gap-3 hover:bg-neutral-50 border-l-2 ${TIER_STYLE[k].row}`}
                      data-testid={`loyalty-leaderboard-row-${k}-${i}`}
                    >
                      <div className="h-9 w-9 rounded-full bg-neutral-100 grid place-items-center text-sm font-bold text-neutral-600 shrink-0">
                        {(v.name || "?").trim()[0]?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-neutral-900 truncate">
                            {v.name}
                          </span>
                          {v.tier && v.tier_medal && (
                            <span className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest ${TIER_STYLE[v.tier].chip}`}>
                              <span aria-hidden>{v.tier_medal}</span>
                              {v.tier_label}
                              {v.tier_discount_pct ? <span className="opacity-70">· {v.tier_discount_pct}%</span> : null}
                            </span>
                          )}
                          {v.is_anonymous && (
                            <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">
                              anonymous
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-neutral-500 mt-0.5">
                          <span className="tabular-nums font-semibold text-neutral-700">{v.total_reactions}</span> reactions
                          <span className="mx-1.5">·</span>
                          {v.up} 👍 · {v.think} 🤔 · {v.nope} 👎
                          <span className="mx-1.5">·</span>
                          {v.distinct_items} items across {v.distinct_shares} carts
                        </div>
                      </div>
                      <button
                        onClick={() => openThank(v)}
                        disabled={v.is_anonymous || !v.has_phone}
                        title={v.is_anonymous ? "Anonymous voter — no delivery address" : v.has_phone ? "Send a thank-you card" : "Voter hasn't shared a phone number"}
                        data-testid={`loyalty-leaderboard-thank-${k}-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <MessageSquareHeart className="h-3.5 w-3.5" /> Thank
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {/* Thank composer modal */}
      {thankTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="loyalty-thank-modal"
          onClick={() => !thankSending && setThankTarget(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-neutral-200">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                  Thank a top scout
                </div>
                <div className="font-semibold text-neutral-900 truncate">
                  {thankTarget.tier_medal ? `${thankTarget.tier_medal} ` : ""}{thankTarget.name}
                </div>
              </div>
              <button
                onClick={() => setThankTarget(null)}
                disabled={thankSending}
                className="p-1 rounded hover:bg-neutral-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex gap-2">
                {(["whatsapp", "sms"] as const).map(ch => (
                  <button
                    key={ch}
                    onClick={() => setThankChannel(ch)}
                    data-testid={`loyalty-thank-channel-${ch}`}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
                      thankChannel === ch
                        ? "border-amber-500 bg-amber-50 text-amber-800"
                        : "border-neutral-200 text-neutral-500 hover:bg-neutral-50"
                    }`}
                  >
                    {ch}
                  </button>
                ))}
              </div>
              <Textarea
                value={thankMessage}
                onChange={e => setThankMessage(e.target.value)}
                placeholder="Say thanks in your own words…"
                rows={6}
                maxLength={1500}
                data-testid="loyalty-thank-textarea"
                className="text-sm"
              />
              <div className="text-[11px] text-neutral-400 text-right tabular-nums">
                {thankMessage.length}/1500
              </div>
            </div>
            <div className="p-4 border-t border-neutral-200 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setThankTarget(null)}
                disabled={thankSending}
              >
                Cancel
              </Button>
              <Button
                onClick={sendThank}
                disabled={thankSending || thankMessage.trim().length < 4}
                data-testid="loyalty-thank-send-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {thankSending
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…</>
                  : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send thank-you</>
                }
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function TierCountCell({ label, count, medal, testid, accent }: {
  label: string; count: number; medal: string; testid?: string; accent?: string;
}) {
  return (
    <div className="p-3 text-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-neutral-500 flex items-center justify-center gap-1">
        <span aria-hidden>{medal}</span> {label}
      </div>
      <div className={`mt-1 font-serif-display text-2xl font-bold tabular-nums ${accent || "text-neutral-900"}`}>
        {count}
      </div>
    </div>
  );
}

export default LoyaltyLeaderboardCard;
